import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { apiPost } from "api/callApi";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  extractZipFiles,
  getFileType,
  readFromLocalStorage,
  showAlert,
} from "utils/helper";
import OccupantNameDropdown from "components/OccupantNameDropdown";
import { useSelector } from "react-redux";
import { InputField } from "@components/";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import SC from "./style";
import CustomBreadcrumb from "components/CustomBreadcrumb";
import OccupantFilterDropdown from "components/OccupantFilterDropdown";
import theme from "@theme/";
import { Button } from "@InnoUI/";
import InnoIcons from "@InnoIcons/";
import { GeneratingText } from "components/GeneratingText";
import { CircularProgress } from "@mui/material";

const MultiUpload = ({ catalogName }) => {
  const [files, setFiles] = useState([]);
  const inputRef = useRef();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const folderId = searchParams.get("folderId");
  const catalogId = searchParams.get("catalogId");
  const type = searchParams.get("type");
  const [data, setData] = useState({ fullName: "" });
  const [selectedOccupantId, setSelectedOccupantId] = useState();
  const [uploading, setUploading] = useState(false);
  const [progess, setProgess] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (String(type) === "1") {
      setSelectedOccupantId(Number(projectId));

      setData((prev) => ({
        ...prev,
        occupantId: Number(projectId),
      }));
    }
  }, [projectId, type]);

  const rawIsBulk = searchParams.get("isbulk");
  const isbulk =
    rawIsBulk === "true" ||
    rawIsBulk === "1" ||
    rawIsBulk === "" ||
    rawIsBulk === null
      ? rawIsBulk === "true" || rawIsBulk === "1"
      : rawIsBulk === "true" || rawIsBulk === "1";

  // -----------------------------
  // add these state hooks near top of component
  const [uploadedFileKeys, setUploadedFileKeys] = useState([]); // strings returned by presigned step
  const [uploadedFilesMeta, setUploadedFilesMeta] = useState([]); // metadata objects to be saved to DB
  // track which indexes (or fileKeys) are currently being removed
  const [removingIndexes, setRemovingIndexes] = useState(new Set());
  const [uploadCompleted, setUploadCompleted] = useState(false);

  const handleChange = (name, value) => {
    console.log(name, value, "klo");

    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangeDropdown = (property, value) => {
    console.log(property, value, "khl");

    setSelectedOccupantId(value);

    // FIX: Store in data object also
    setData((prev) => ({
      ...prev,
      occupantId: value,
    }));
  };

  const allowedTypes = ["image/", "video/"]; // keep for non-bulk validation

  // Allowed image/video mime-type prefix
  const allowedMimePrefixes = ["image/", "video/"];

  // Small whitelist of extensions to allow if mime type missing/unreliable
  const allowedExts = new Set([
    "png",
    "jpg",
    "jpeg",
    "gif",
    "webp",
    "bmp",
    "tif",
    "tiff",
    "heic",
    "svg",
    "mp4",
    "mov",
    "avi",
    "mkv",
    "webm",
    "flv",
    "mpeg",
    "mpg",
    "3gp",
  ]);

  // Helper: create File object + preview URL from extracted buffer object
  // expects ef: { fileName, fileType, fileSize, buffer }
  const makeFileEntryFromExtracted = (ef) => {
    const baseName = getBaseName(ef.fileName);
    const mime = ef.fileType || getFileType(ef) || "application/octet-stream";
    const blob = new Blob([ef.buffer], { type: mime });
    const fileObj = new File([ef.buffer], baseName, { type: mime });

    return {
      file: fileObj,
      preview: URL.createObjectURL(blob),
      progress: 0,
      status: "ready",
      cancelToken: null,
      response: null,
      _extractedMeta: {
        originalName: ef.fileName,
        fileType: ef.fileType,
        fileSize: ef.fileSize,
      },
    };
  };

  // Extract zip file(s), filter and add contained images/videos to files state
  const extractAndAddZip = async (zipFile) => {
    try {
      const extracted = await extractZipFiles(zipFile); // your helper
      if (!Array.isArray(extracted) || extracted.length === 0) {
        showAlert({
          title: "Warning",
          text: "No files found inside the ZIP.",
          icon: "warning",
        });
        return;
      }

      // filter out macOS/hidden junk first
      const cleaned = extracted.filter((ef) => isValidFile(ef.fileName));

      // strict media filter: mime first, then small-extension whitelist
      const mediaFiles = cleaned.filter((ef) => {
        const type = String(ef.fileType || getFileType(ef) || "")
          .toLowerCase()
          .trim();

        // allow if mime says image/ or video/
        if (allowedMimePrefixes.some((p) => type.startsWith(p))) return true;

        // fallback: allow only if extension is in allowedExts
        const ext = String(ef.fileName || "")
          .split(".")
          .pop()
          .toLowerCase()
          .trim();

        return allowedExts.has(ext);
      });

      if (!mediaFiles.length) {
        showAlert({
          title: "Warning",
          text: "No images or videos found inside the ZIP.",
          icon: "warning",
        });
        return;
      }

      // Create File-style entries for each extracted item and append to files state
      const fileEntries = mediaFiles.map((ef) =>
        makeFileEntryFromExtracted(ef),
      );
      // addFiles expects an array of File objects; we already created the 'file' entries shape
      // If your addFiles expects raw File[], we can call addFiles(fileEntries.map(e=>e.file))
      // But since your UI expects objects with preview/progress/status, merge them:
      setFiles((prev) => [...prev, ...fileEntries]);
    } catch (err) {
      console.error("Failed to extract ZIP:", err);
      showAlert({
        title: "Error",
        text: "Failed to extract ZIP file. Make sure it's a valid archive.",
        icon: "error",
      });
    }
  };

  // handle input selection (click -> file browser)
  const handleSelect = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;

    setUploadCompleted(false);

    // If bulk mode, only allow zip files. If user tried to select non-zip, warn and ignore.
    if (isbulk) {
      const nonZip = selected.some(
        (f) =>
          !(
            f.type === "application/zip" ||
            f.name?.toLowerCase().endsWith(".zip") ||
            f.name?.toLowerCase().endsWith(".jar")
          ),
      );
      if (nonZip) {
        showAlert({
          title: "Warning",
          text: "In bulk mode only ZIP files are allowed. Please select a ZIP containing images/videos.",
          icon: "warning",
        });
        e.target.value = null;
        return;
      }

      for (const zipFile of selected) {
        await extractAndAddZip(zipFile);
      }

      e.target.value = null;
      return;
    }

    // non-bulk: accept only images/videos
    const validFiles = selected.filter((file) =>
      allowedTypes.some((type) => file.type.startsWith(type)),
    );

    if (validFiles.length !== selected.length) {
      showAlert({
        title: "Warning",
        text: "Only images and videos are allowed.",
        icon: "warning",
      });
    }

    // convert to your file-entry shape (preserve previews & initial metadata)
    const entries = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      progress: 0,
      status: "ready",
      cancelToken: null,
      response: null,
    }));

    setFiles((prev) => [...prev, ...entries]);
    e.target.value = null; // reset so same file selection works again
  };

  // handle drop (drag & drop)
  const handleDrop = async (e) => {
    e.preventDefault();

    setUploadCompleted(false);

    const dropped = Array.from(e.dataTransfer.files || []);
    if (!dropped.length) return;

    if (isbulk) {
      const nonZip = dropped.some(
        (f) =>
          !(
            f.type === "application/zip" ||
            f.name?.toLowerCase().endsWith(".zip") ||
            f.name?.toLowerCase().endsWith(".jar")
          ),
      );
      if (nonZip) {
        showAlert({
          title: "Warning",
          text: "In bulk mode only ZIP files are allowed. Please drop a ZIP containing images/videos.",
          icon: "warning",
        });
        return;
      }

      for (const zipFile of dropped) {
        await extractAndAddZip(zipFile);
      }
      return;
    }

    // non-bulk: accept only images/videos
    const validFiles = dropped.filter((file) =>
      allowedTypes.some((type) => file.type.startsWith(type)),
    );

    if (validFiles.length !== dropped.length) {
      showAlert({
        title: "Warning",
        text: "Only images and videos are allowed.",
        icon: "warning",
      });
    }

    const entries = validFiles.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      progress: 0,
      status: "ready",
      cancelToken: null,
      response: null,
    }));

    setFiles((prev) => [...prev, ...entries]);
  };

  const saveToDatabase = async () => {
    if (!uploadedFilesMeta.length) {
      showAlert({
        title: "Info",
        text: "No uploaded files to save",
        icon: "info",
      });
      return;
    }

    setLoading(true);

    try {
      // build common files array
      const filesPayload = uploadedFilesMeta.map((f) => ({
        fileName: f.fileName,
        fileType: f.fileType,
        fileUrl: f.fileUrl,
        fileSize: f.fileSize,
      }));

      let res;

      if (catalogId) {
        /* ✅ ADD FILES TO EXISTING CATALOG */
        const payload = {
          catalogId,
          files: filesPayload,
        };

        res = await apiPost("/v1/photos/add-existing-catalog-files", payload);
      } else {
        /* ✅ OLD FLOW – CREATE NEW CATALOG */
        // Occupant is optional: when none is picked, the catalog is saved
        // at the project level (occupantId null, projectType 0) instead.
        const payload = {
          files: uploadedFilesMeta.map((f) => ({
            ...f,
            occupantId: data.occupantId || null,
            folderId,
            catalogName: data.catalogName,
            date: data.date,
            projectId,
            projectType: data.occupantId ? 1 : 0,
          })),
        };

        res = await apiPost("/v1/uploadFiles", payload);
      }

      showAlert({
        title: "Saved",
        text: res?.message || "Files saved successfully",
        icon: "success",
      });

      navigate(-1);

      /* ✅ RESET STATE */
      setUploadedFilesMeta([]);
      setUploadedFileKeys([]);
      setFiles([]);
      setProgess(0);

      setData({
        fullName: "",
        catalogName: "",
        date: "",
        occupantId: "",
      });

      setSelectedOccupantId(undefined);
    } catch (err) {
      console.error("Failed to save to DB", err);
      showAlert({
        title: "Error",
        text: err?.response?.data?.message || "Failed to save to database",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const removeFile = async (index) => {
    const f = files[index];

    setUploadCompleted(false);

    // cancel token if uploading
    if (f.cancelToken) f.cancelToken.cancel("Cancelled");

    // if file already uploaded (we saved fileKey in response), call delete API
    const fileKey = f?.response?.fileKey || f?.response?.data?.filekey || null;

    if (fileKey) {
      try {
        // mark this index as removing
        setRemovingIndexes((prev) => {
          const next = new Set(prev);
          next.add(index);
          return next;
        });
        // call delete api to remove from S3
        await apiPost("/v1/aws/file/deletefile", { fileKey });
        // remove from uploaded keys/meta
        setUploadedFileKeys((prev) => prev.filter((k) => k !== fileKey));
        setUploadedFilesMeta((prev) =>
          prev.filter((m) => m.fileUrl !== fileKey),
        );
      } catch (err) {
        console.warn("Failed to delete file from S3", err);
        // optionally show error but still remove from UI if you want
        showAlert({
          title: "Warning",
          text: "Failed to delete file from storage. Please try again.",
          icon: "warning",
        });
        return; // stop removing from UI if you want to keep consistency
      } finally {
        setRemovingIndexes((prev) => {
          const next = new Set(prev);
          next.delete(index);
          return next;
        });
      }
    }

    URL.revokeObjectURL(f.preview);

    // remove from files list
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const {
    REACT_APP_BASE_URL: baseURL,
    REACT_APP_REQUEST_TIMEOUT: timeout,
    REACT_APP_API_KEY,
  } = process.env;

  let accessToken = REACT_APP_API_KEY;
  let userAuth = readFromLocalStorage("userAccessToken");
  let BASE_URL = baseURL || `${window.location.origin}/api`;
  userAuth = userAuth === null ? "" : userAuth;

  const getBaseName = (name = "") => {
    const parts = name.split(/[\\/]/);
    return parts[parts.length - 1] || "";
  };

  const isValidFile = (name = "") => {
    const base = getBaseName(name);

    if (!base) return false;
    if (base === "__MACOSX") return false;
    if (base.startsWith("__MACOSX")) return false;
    if (base.startsWith("._")) return false; // macOS resource fork
    if (base.startsWith(".")) return false; // hidden file

    return true;
  };

  const uploadAll = async () => {
    const filesToUpload = files
      .map((f, index) => ({ ...f, index }))
      .filter((f) => f.status !== "done");

    if (!filesToUpload.length) {
      showAlert({
        title: "Info",
        text: "No new files to upload",
        icon: "info",
      });
      return;
    }

    setUploading(true);
    setProgess(0);

    const totalSize = filesToUpload.reduce((s, f) => s + (f.file.size || 0), 0);

    let uploadedBytes = 0;
    const newUploadedKeys = [];
    const newUploadedFiles = [];

    try {
      for (const item of filesToUpload) {
        const i = item.index;
        const f = item;

        setFiles((prev) => {
          const copy = [...prev];
          copy[i] = { ...copy[i], status: "uploading", progress: 0 };
          return copy;
        });

        // presigned url
        const presignedRes = await apiPost("/v1/photos/fileuploadurl", {
          fileName: f.file.name,
          fileType: f.file.type,
          folderId,
        });

        const { uploadUrl, filekey } = presignedRes;

        await axios.put(uploadUrl, f.file, {
          headers: { "Content-Type": f.file.type },
          onUploadProgress: (evt) => {
            const percent = evt.total
              ? Math.round((evt.loaded / evt.total) * 100)
              : 0;

            setFiles((prev) => {
              const copy = [...prev];
              copy[i] = { ...copy[i], progress: percent };
              return copy;
            });

            uploadedBytes += evt.loaded - (f._lastLoaded || 0);
            f._lastLoaded = evt.loaded;

            setProgess(Math.round((uploadedBytes / totalSize) * 100));
          },
        });

        newUploadedKeys.push(filekey);
        newUploadedFiles.push({
          fileName: f.file.name,
          fileType: f.file.type,
          fileSize: f.file.size,
          fileUrl: filekey,
        });

        setFiles((prev) => {
          const copy = [...prev];
          copy[i] = {
            ...copy[i],
            status: "done",
            progress: 100,
            response: { fileKey: filekey },
          };
          return copy;
        });
      }

      setUploadedFileKeys((prev) => [...prev, ...newUploadedKeys]);
      setUploadedFilesMeta((prev) => [...prev, ...newUploadedFiles]);
    } catch (err) {
      showAlert({
        title: "Error",
        text: "Upload failed",
        icon: "error",
      });
    } finally {
      setUploading(false);
      setProgess(0);
    }
  };

  const occupantOption = useSelector((state) =>
    Array.isArray(state?.occupantOption?.data)
      ? state?.occupantOption?.data
      : [],
  );

  console.log(occupantOption, "occupantOption");

  const nameOption = occupantOption.map((occ) => ({
    id: occ.id,
    label: occ.occupantName,
  }));

  const loaderOverlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.4)",
    backdropFilter: "blur(6px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  };

  const loaderBoxStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  };

  const loaderSpinner = {
    width: "70px",
    height: "70px",
    border: "6px solid rgba(255,255,255,0.3)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  };

  {
    uploading && (
      <div style={loaderOverlayStyle}>
        <div style={loaderBoxStyle}>
          <div style={loaderSpinner}></div>
          <p style={{ marginTop: 15, color: "#fff", fontSize: 16 }}>
            Uploading files, please wait...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <CustomBreadcrumb />

      <SC.BodyContainer>
        <SC.FilterContainer>
          <OccupantFilterDropdown />
        </SC.FilterContainer>
      </SC.BodyContainer>

      <div
        style={{
          margin: "0 auto",
          padding: "20px",
          fontFamily: "Inter, sans-serif",
        }}
      >
        {/* ---------------- Title ---------------- */}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <SC.HeaderLeft>
            <InnoIcons.ArrowBackIcon
              size={20}
              style={{ cursor: "pointer", marginRight: "10px" }}
              onClick={() => navigate(-1)}
            />
            <SC.Title> Upload Photos & Videos</SC.Title>
          </SC.HeaderLeft>
        </div>

        {/* ---------------- Form Container ---------------- */}

        <div
          style={{
            background: "rgba(255,255,255,0.7)",
            backdropFilter: "blur(10px)",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
            border: "1px solid rgba(255,255,255,0.4)",
          }}
        >
          {!catalogId && (
            <>
              {/* Occupant Select */}
              <div
                style={{
                  marginBottom: "15px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <label style={{ fontWeight: 500 }}>
                  Select Occupant{" "}
                  {type == 1 ? (
                    <span style={{ color: "red", fontWeight: 600 }}>*</span>
                  ) : (
                    <span style={{ color: "#888", fontWeight: 400 }}>
                      (optional — leave blank for a project-level catalog)
                    </span>
                  )}
                </label>

                <div style={{ marginTop: "6px" }}>
                  <OccupantNameDropdown
                    name="occupantName"
                    onChange={handleChangeDropdown}
                    defaultValue={selectedOccupantId}
                    nameOption={nameOption}
                    disabledDropdown={type == 1}
                  />
                </div>
              </div>
              {/* Date Picker */}
              <div
                style={{
                  marginBottom: "15px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "100px",
                }}
              >
                <div
                  style={{
                    marginBottom: "15px",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <label style={{ fontWeight: 500 }}>
                    Select Date{" "}
                    <span style={{ color: "red", fontWeight: 600 }}>*</span>
                  </label>

                  <div style={{ marginTop: "6px" }}>
                    <DatePicker
                      name="date"
                      value={data.date ? dayjs(data.date, "YYYY-MM-DD") : null}
                      format="YYYY-MM-DD"
                      onChange={(value) => {
                        const formattedDate = value
                          ? dayjs(value).format("YYYY-MM-DD")
                          : "";
                        handleChange("date", formattedDate);
                      }}
                      style={{
                        width: "260px",
                        height: "38px",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                </div>

                {/* Catalog Name */}
                <div
                  style={{
                    marginBottom: "15px",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <label style={{ fontWeight: 500 }}>
                    Catalog Name{" "}
                    <span style={{ color: "red", fontWeight: 600 }}>*</span>
                  </label>

                  <InputField
                    name="catalogName"
                    value={data.catalogName}
                    onChange={handleChange}
                    style={{
                      width: "260px",
                      height: "38px",
                      borderRadius: "8px",
                      marginTop: "6px",
                    }}
                  />
                </div>
              </div>
            </>
          )}

          {/* ---------------- Drag Drop Box ---------------- */}
          <p
            style={{
              fontWeight: 500,
              marginBottom: 5,
              color: "red",
              fontSize: "14px",
              fontStyle: "italic",
            }}
          >
            {isbulk ? "Upload only ZIP file" : "Upload images or videos"}
          </p>
          <div
            style={{
              marginBottom: "15px",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              vertircalAlign: "top",
              gap: "10px",
            }}
          >
            <label style={{ fontWeight: 500 }}> Files</label>

            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => inputRef.current.click()}
              style={{
                border: "3px dashed #bbb",
                borderRadius: "12px",
                padding: "20px",
                textAlign: "center",
                // marginTop: "20px",
                cursor: "pointer",
                background: "rgba(255,255,255,0.5)",
                transition: "0.3s",
                width: "100%",
                height: "100px",
              }}
              className="dropzone"
            >
              <h3 style={{ margin: 0, fontWeight: 600, color: "#666" }}>
                Drag & Drop Files Here
              </h3>
              <p style={{ marginTop: "6px", color: "#999" }}>
                or click to browse
              </p>

              <input
                type="file"
                multiple
                ref={inputRef}
                accept={isbulk ? ".zip" : "image/*,video/*"}
                style={{ display: "none" }}
                onChange={handleSelect}
              />
            </div>
          </div>
        </div>

        {/* ---------------- Files Preview List ---------------- */}
        <div style={{ marginTop: 20 }}>
          {files?.map((f, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                gap: 15,
                borderBottom: "1px solid #eee",
                padding: "10px 0",
                alignItems: "center",
              }}
            >
              {/* Preview */}
              <div style={{ width: 50, height: 40, overflow: "hidden" }}>
                {f?.file?.type.startsWith("image") ? (
                  <img
                    src={f.preview}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : f?.file?.type.startsWith("video") ? (
                  <video
                    src={f?.preview}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      background: "#ddd",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    File
                  </div>
                )}
              </div>

              {/* Info + Progress */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div>
                  <strong style={{ fontSize: "13px" }}>{f?.file?.name}</strong>{" "}
                  <span style={{ color: "#777" }}>
                    ({(f?.file?.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>

                {/* Progress bar */}
                <div
                  style={{
                    background: "#eee",
                    height: 6,
                    borderRadius: 4,
                    marginTop: 8,
                  }}
                >
                  <div
                    style={{
                      width: `${f?.progress}%`,
                      height: "100%",
                      background:
                        f.status === "error"
                          ? "#e74c3c"
                          : theme.colors.themeColor,
                      borderRadius: 4,
                      transition: "width 0.3s ease-in-out",
                    }}
                  />
                </div>

                {/* Status */}
                <div style={{ marginTop: 6, fontSize: 12 }}>
                  Status:{" "}
                  {f?.status === "ready" && (
                    <span style={{ color: "#555" }}>Ready to upload</span>
                  )}
                  {f?.status === "uploading" && (
                    <span style={{ color: "#0b74de" }}>Uploading...</span>
                  )}
                  {f?.status === "done" && (
                    <span style={{ color: "green", fontWeight: 600 }}>
                      Uploaded ✓
                    </span>
                  )}
                  {f?.status === "error" && (
                    <span style={{ color: "red" }}>{f?.response}</span>
                  )}
                </div>
              </div>

              {/* Remove / Cancel Button */}
              <div>
                {f?.status === "uploading" ? null : (
                  <Button
                    caption={
                      removingIndexes.has(idx) ? "Removing..." : "Remove"
                    }
                    color="white"
                    fontSize="12px"
                    style={{
                      marginBottom: 8,
                      background: theme.gradient.themeGradient,
                      color: theme.colors.white,
                      height: "28px",
                      borderRadius: "3px",
                    }}
                    onClick={() => removeFile(idx)}
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* ---------------- Upload Button ---------------- */}
        <div
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "flex-end",
            marginTop: 20,
          }}
        >
          {files.length > 0 && (
            <Button
              caption={uploading ? "Uploading..." : "Upload ALL"}
              onClick={uploadAll}
              isLoading={loading}
              // disabled={uploading}
              disabled={uploading || uploadCompleted}
              type="submit"
              color="white"
              fontSize="12px"
              style={{
                background: "#4caf50",
                color: theme.colors.white,
                minWidth: "90px",
                height: "30px",
                borderRadius: "3px",
                cursor: "pointer",
              }}
            />
          )}

          <Button
            leftIcon={
              loading ? (
                <CircularProgress
                  size={14}
                  thickness={5}
                  sx={{ color: theme.colors.themeColor }}
                />
              ) : null
            }
            caption={loading ? <GeneratingText label="Saving" /> : "Save"}
            onClick={saveToDatabase}
            isLoading={loading}
            disabled={loading || uploadedFilesMeta.length === 0}
            type="submit"
            color="white"
            fontSize="12px"
            style={{
              background: theme.gradient.themeGradient,
              color: theme.colors.white,
              minWidth: "90px",
              height: "30px",
              borderRadius: "3px",
              cursor:
                uploadedFilesMeta.length === 0 ? "not-allowed" : "pointer",
              opacity: uploadedFilesMeta.length === 0 ? 0.6 : 1,
            }}
          />
        </div>
      </div>
    </>
  );
};

export default MultiUpload;
